import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, MessageCircle, Download } from 'lucide-react';

import {
  PerformanceSummary,
  ConversationIntelligence,
  ActionCenter,
  EngagementTimeline,
  EngagementScore,
  BusinessImpact,
  HiddenInsights,
  ShareholderReport
} from './components';
import ReportedIssues from './ReportedIssues';
import { useUser } from '../../../../../../context/UserContext';
import logger from '../../../../../../utils/logger';

import './Dashboard.css';

/**
 * Analytics Dashboard 2.0 - Unforgettable Experience
 * 
 * Organized to answer user questions AND provide:
 * - Gamification (daily score, streaks, challenges)
 * - Business Impact (shareholder-ready ROI)
 * - Hidden Insights (what competitors won't tell you)
 */
const Dashboard = ({ selectedChatbotId: propSelectedChatbotId }) => {
  const { getPlanDisplayName, userRole, user } = useUser();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [chatbots, setChatbots] = useState([]);
  const [selectedChatbot, setSelectedChatbot] = useState(propSelectedChatbotId || 'all');
  const [reportedIssuesCount, setReportedIssuesCount] = useState(0);
  const [showReport, setShowReport] = useState(false);

  const fetchChatbots = useCallback(async () => {
    if (!user) return;

    try {
      const response = await fetch(`/api/user/chatbots?userId=${user.uid}`);
      if (!response.ok) throw new Error('Failed to fetch chatbots');
      const data = await response.json();
      setChatbots(data.chatbots || []);
    } catch (err) {
      logger.error('Error fetching chatbots:', err);
      setChatbots([]);
    }
  }, [user]);

  useEffect(() => {
    if (propSelectedChatbotId) {
      setSelectedChatbot(propSelectedChatbotId);
    }
  }, [propSelectedChatbotId]);

  const fetchAnalytics = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        userId: user.uid,
        timeRange: timeRange
      });

      if (selectedChatbot !== 'all') {
        params.append('chatbotId', selectedChatbot);
      }

      const response = await fetch(`/api/dashboard/analytics?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setAnalytics(data.analytics);
    } catch (err) {
      logger.error('Error fetching analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, timeRange, selectedChatbot]);

  // Fetch reported issues count
  useEffect(() => {
    const fetchIssuesCount = async () => {
      if (!selectedChatbot || selectedChatbot === 'all') {
        setReportedIssuesCount(0);
        return;
      }
      try {
        const response = await fetch(`/api/issues/${selectedChatbot}`);
        if (response.ok) {
          const data = await response.json();
          setReportedIssuesCount(data.issues?.length || 0);
        }
      } catch (err) {
        logger.error('Error fetching issues count:', err);
      }
    };
    fetchIssuesCount();
  }, [selectedChatbot]);

  useEffect(() => {
    fetchChatbots();
  }, [fetchChatbots]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const getChatbotLabel = () => {
    if (selectedChatbot === 'all') return 'All Chatbots';
    const chatbot = chatbots.find(c => c.chatbot_id === selectedChatbot);
    return chatbot?.config?.assistantName || `Chatbot ${selectedChatbot.slice(0, 8)}...`;
  };

  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case '7d': return 'Last 7 days';
      case '30d': return 'Last 30 days';
      case '90d': return 'Last 90 days';
      default: return 'Last 30 days';
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="analytics-container">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
              <BarChart3 size={26} className="text-violet-600" />
            </div>
            <p className="text-sm text-slate-500">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="analytics-container">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <MessageCircle size={26} className="text-rose-500" />
            </div>
            <p className="text-sm font-medium text-slate-700 mb-2">Unable to load analytics</p>
            <p className="text-xs text-slate-400 mb-4">{error}</p>
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Analytics</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            {getChatbotLabel()} · {getTimeRangeLabel()}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Time Range Segmented Control */}
          <div className="flex p-1 bg-slate-100/80 rounded-lg">
            {['7d', '30d', '90d'].map((range) => (
              <button
                key={range}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${timeRange === range
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
                  }`}
                onClick={() => setTimeRange(range)}
              >
                {range === '7d' ? '7D' : range === '30d' ? '30D' : '90D'}
              </button>
            ))}
          </div>

          {/* Chatbot Selector */}
          {!propSelectedChatbotId && chatbots.length > 0 && (
            <select
              value={selectedChatbot}
              onChange={(e) => setSelectedChatbot(e.target.value)}
              className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">All Chatbots</option>
              {chatbots.map((chatbot) => (
                <option key={chatbot.chatbot_id} value={chatbot.chatbot_id}>
                  {chatbot.config?.assistantName || `Chatbot ${chatbot.chatbot_id.slice(0, 8)}...`}
                </option>
              ))}
            </select>
          )}

          {/* Export Button */}
          <button
            onClick={() => setShowReport(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-200 transition-colors"
            title="Export Report"
          >
            <Download size={14} />
            Export
          </button>

          {/* Plan Badge */}
          <span className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${userRole === 'starter'
            ? 'bg-slate-100 text-slate-500'
            : 'bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-sm'
            }`}>
            {getPlanDisplayName()}
          </span>
        </div>
      </div>

      {/* Row 1: Engagement Score + Performance Summary */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <EngagementScore
          analytics={analytics}
          chatbotId={selectedChatbot !== 'all' ? selectedChatbot : null}
          userId={user?.uid}
        />
        <PerformanceSummary analytics={analytics} />
      </section>

      {/* Customer Satisfaction — 👍 / 👎 feedback on bot replies */}
      <section className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-slate-900">Customer Satisfaction</h3>
            <p className="text-xs text-slate-500">Based on 👍 / 👎 feedback visitors left on bot replies</p>
          </div>
          {analytics?.feedback?.totalRated > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <div className="text-3xl font-bold text-slate-900">{analytics.feedback.satisfactionRate}%</div>
                <p className="text-xs text-slate-500 mt-1">Satisfaction</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-600">{analytics.feedback.thumbsUp}</div>
                <p className="text-xs text-slate-500 mt-1">👍 Helpful</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-500">{analytics.feedback.thumbsDown}</div>
                <p className="text-xs text-slate-500 mt-1">👎 Not helpful</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-slate-900">{analytics.feedback.totalRated}</div>
                <p className="text-xs text-slate-500 mt-1">Total rated</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No feedback yet in this period. Ratings appear here once visitors use the 👍 / 👎 buttons on replies.</p>
          )}
        </div>
      </section>

      {/* Row 2: Conversation Intelligence + Action Center */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <ConversationIntelligence
            topics={analytics?.topTopics || []}
            totalConversations={analytics?.messageVolume?.totalConversations || 0}
          />
        </div>
        <div className="lg:col-span-1">
          <ActionCenter
            ticketMetrics={analytics?.ticketMetrics || {}}
            reportedIssuesCount={reportedIssuesCount}
            onNavigateInbox={() => { }}
            onViewIssues={() => {
              document.getElementById('reported-issues')?.scrollIntoView({ behavior: 'smooth' });
            }}
          />
        </div>
      </section>

      {/* Row 3: Business Impact + Hidden Insights */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <BusinessImpact analytics={analytics} hourlyRate={30} />
        <HiddenInsights
          analytics={analytics}
          dailyActivity={analytics?.dailyActivity || []}
          userId={user?.uid}
          chatbotId={selectedChatbot !== 'all' ? selectedChatbot : null}
          userEmail={user?.email}
        />
      </section>

      {/* Row 4: Engagement Timeline (Full Width) */}
      <section className="mb-6">
        <EngagementTimeline
          dailyActivity={analytics?.dailyActivity || []}
          geographic={analytics?.geographic || []}
          device={analytics?.device || []}
        />
      </section>

      {/* Row 5: Reported Issues */}
      {selectedChatbot !== 'all' && (
        <section id="reported-issues">
          <ReportedIssues chatbotId={selectedChatbot} />
        </section>
      )}

      {/* PDF Export Modal */}
      {showReport && (
        <ShareholderReport
          analytics={analytics}
          engagementScore={analytics?.ticketMetrics ? 50 : 0}
          chatbotName={chatbots.find(c => c.chatbot_id === selectedChatbot)?.config?.assistantName || 'Chatbot'}
          dateRange={timeRange === '7d' ? 'Last 7 Days' : timeRange === '30d' ? 'Last 30 Days' : 'Last 90 Days'}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;